/**
 * N75 (Alana): "no agendamento do hospital, incluir um campo que permita
 * visualizar e copiar todos os e-mails dos responsáveis cadastrados no hospital
 * e/ou cliente vinculado à contagem".
 *
 * Só agrega o que a aba de Agendamento já carregou (hospital, clientes e locais
 * selecionados) — sem endpoint novo, e respeitando o vínculo do usuário, já que
 * os dados vêm das chamadas existentes.
 */
import React, { useMemo, useState } from 'react';
import { Mail, Copy, Check } from 'lucide-react';
import type { Estabelecimento, LocalContagem } from '../../types/estabelecimento';
import type { Cliente } from '../../services/clienteService';

interface Props {
  hospital?: Estabelecimento;
  clientes: Cliente[];
  locais: LocalContagem[];
}

/** Separa listas do tipo "a@x.com; b@y.com" e descarta vazios. */
function split(valor?: string | null): string[] {
  return (valor || '')
    .split(/[;,]/)
    .map((e) => e.trim())
    .filter((e) => e.includes('@'));
}

export const EmailsResponsaveisField: React.FC<Props> = ({ hospital, clientes, locais }) => {
  const [copiado, setCopiado] = useState(false);
  // N75 (reteste 24/07): a lista de cópia do cliente (`email_copia`) trazia
  // endereços internos da equipe, que a Alana pediu para não constar. Deixou de
  // entrar por padrão; fica atrás deste toggle para não perder o caso de uso.
  const [incluirCopia, setIncluirCopia] = useState(false);

  const nomeHospital = hospital?.nome_fantasia || 'Hospital';

  const emails = useMemo(() => {
    const itens: { email: string; origem: string }[] = [];
    const push = (valor: string | undefined | null, origem: string) =>
      split(valor).forEach((email) => itens.push({ email, origem }));

    // Hospital: e-mail principal + contatos adicionais cadastrados (N75).
    push(hospital?.email, nomeHospital);
    (hospital?.contatos || []).forEach((c) =>
      push(c.email, c.nome ? `${nomeHospital} — ${c.nome}` : nomeHospital),
    );

    // Pontos de contagem: responsável + contatos adicionais do ponto (N75).
    locais.forEach((l) => {
      push(l.email, l.responsavel ? `${l.local} — ${l.responsavel}` : l.local);
      (l.contatos_extras || []).forEach((c) =>
        push(c.email, c.nome ? `${l.local} — ${c.nome}` : l.local),
      );
    });

    clientes.forEach((c) => {
      const nome = c.nome_fantasia || c.razao_social || 'Cliente';
      push(c.email, nome);
      if (incluirCopia) push(c.email_copia, `${nome} (cópia)`);
    });

    // dedup por e-mail (case-insensitive), mantendo a primeira origem
    const vistos = new Set<string>();
    return itens.filter(({ email }) => {
      const chave = email.toLowerCase();
      if (vistos.has(chave)) return false;
      vistos.add(chave);
      return true;
    });
  }, [hospital, clientes, locais, incluirCopia, nomeHospital]);

  // Só oferece o toggle se algum cliente realmente tiver lista de cópia.
  const temCopia = clientes.some((c) => split(c.email_copia).length > 0);

  const copiar = async () => {
    try {
      await navigator.clipboard.writeText(emails.map((e) => e.email).join('; '));
      setCopiado(true);
      setTimeout(() => setCopiado(false), 2000);
    } catch {
      /* clipboard bloqueado pelo navegador — o usuário ainda pode selecionar o texto */
    }
  };

  return (
    <div className="col-span-full">
      <div className="flex items-center justify-between mb-1">
        <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300">
          <Mail size={16} />
          E-mails dos responsáveis ({emails.length})
        </label>
        <div className="flex items-center gap-3">
          {/* N75: lista de cópia do cliente fica fora por padrão. */}
          {temCopia && (
            <label className="flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-400 cursor-pointer">
              <input
                type="checkbox"
                checked={incluirCopia}
                onChange={(e) => setIncluirCopia(e.target.checked)}
                className="rounded border-gray-300 dark:border-gray-600"
              />
              Incluir e-mails de cópia do cliente
            </label>
          )}
          {emails.length > 0 && (
            <button
              type="button"
              onClick={copiar}
              className="flex items-center gap-1 text-sm px-2 py-1 rounded text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 transition-colors"
            >
              {copiado ? <Check size={14} /> : <Copy size={14} />}
              {copiado ? 'Copiado!' : 'Copiar todos'}
            </button>
          )}
        </div>
      </div>

      {emails.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-gray-400 italic">
          Nenhum e-mail cadastrado para o hospital, locais ou clientes desta contagem.
        </p>
      ) : (
        <div className="max-h-32 overflow-y-auto rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 p-2 space-y-1">
          {emails.map(({ email, origem }) => (
            <div key={email} className="flex justify-between gap-2 text-sm">
              <span className="text-gray-900 dark:text-gray-100 select-all break-all">{email}</span>
              <span className="text-xs text-gray-500 dark:text-gray-400 shrink-0">{origem}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
